import * as React from 'react'
import { StyleSheet, View, Text } from 'react-native'
import { Link } from 'expo-router'

import { createColumnHelper, flexRender, getCoreRowModel, PaginationState, useReactTable } from '@tanstack/react-table'
import { DS } from '../../design-system'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../models'
import { CreatorModelType, DashboardGameModel, PlayerInfoModelType } from '../../models/api-store'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { monthDayAndTimeFormatterWithYear } from '../../utils'
import { getShareBaseUrl } from '../../utils/shareUrl'
import { getSnapshot, SnapshotOut } from 'mobx-state-tree'
import { useRouter } from 'expo-router'
import { Pressable } from 'react-native-gesture-handler'
import { useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'

type DataSource = 'legacy' | 'convex'
type GameModelType = SnapshotOut<typeof DashboardGameModel>
const columnHelper = createColumnHelper<GameModelType>()

const CreatorCell = ({ creator, type }: { creator: CreatorModelType; type: string }) => {
  const router = useRouter()

  return (
    <View>
      {creator.name !== 'System' && (
        <View>
          <Pressable
            onPress={() => creator?.id && router.push(`/dashboard/users/${encodeURIComponent(creator.id)}`)}
            style={{ alignItems: 'flex-start' }}
          >
            <DS.Text variant="Body Small/Medium">{creator?.name}</DS.Text>
          </Pressable>
          <DS.Text variant="Body Small/Medium">{type}</DS.Text>
        </View>
      )}
      {creator.name === 'System' && (
        <View>
          <DS.Text variant="Body Small/Medium">{creator?.name}</DS.Text>
          <DS.Text variant="Body Small/Medium">{type}</DS.Text>
        </View>
      )}
    </View>
  )
}

const PlayersCell = ({ players }: { players: PlayerInfoModelType[] }) => {
  const router = useRouter()

  return (
    <View style={styles.playersContainer}>
      {players.map((player, index) => {
        const playerId = player.id
        const playerName = player.name || '-'

        if (!playerId) {
          return (
            <View style={styles.player} key={`${playerName}-${index}`}>
              <DS.Text variant="Body Small/Regular" color="Admin/Icon">
                {playerName}
              </DS.Text>
            </View>
          )
        }

        return (
          <Pressable onPress={() => router.push(`/dashboard/users/${encodeURIComponent(playerId)}`)} key={playerId}>
            <View style={styles.player}>
              <DS.Text variant="Body Small/Regular" color="Admin/Icon">
                {playerName}
              </DS.Text>
            </View>
          </Pressable>
        )
      })}
    </View>
  )
}

const BotsCell = ({ bots }: { bots: string }) => {
  return (
    <View style={styles.botsContainer}>
      <DS.Text variant="Body Small/Regular" color="Admin/Icon">
        {bots}
      </DS.Text>
    </View>
  )
}

const GameHashCell = ({ hash }: { hash: string }) => {
  const router = useRouter()

  return (
    <Link href={`/game/join/${hash}`} target="_blank">
      {hash}
    </Link>
  )
}

const columns = [
  columnHelper.accessor('gameId', {
    header: () => 'Game ID',
    cell: (info) => {
      const gameId = info.getValue()
      if (!gameId) {
        return (
          <DS.Text variant="Body Small/Regular" color="Admin/Icon">
            -
          </DS.Text>
        )
      }
      return (
        <Link href={`/game/${gameId}`} target="_blank">
          <DS.Text selectable variant="Body Small/Regular" color="Admin/Icon">
            {gameId}
          </DS.Text>
        </Link>
      )
    },
    footer: (info) => info.column.id
  }),
  columnHelper.accessor('hash', {
    header: () => 'Share Code',
    cell: (info) => <GameHashCell hash={info.getValue() as string} />,
    footer: (info) => info.column.id
  }),
  columnHelper.accessor('createdAt', {
    header: () => 'Created At',
    cell: (info) => (
      <DS.Text selectable variant="Body Small/Regular">
        {info.getValue() ? monthDayAndTimeFormatterWithYear(new Date(info.getValue())) : 'N/A'}
      </DS.Text>
    ),
    footer: (info) => info.column.id
  }),
  columnHelper.accessor('creator', {
    id: 'creator',
    cell: ({ row }) => (
      <CreatorCell creator={row.original.creator as CreatorModelType} type={row.original.gameType || ''} />
    ),
    header: () => <span>Creator</span>,
    footer: (info) => info.column.id
  }),
  columnHelper.accessor('players', {
    header: () => 'Players In Game',
    cell: (info) => <PlayersCell players={info.getValue() as PlayerInfoModelType[]} />,
    footer: (info) => info.column.id
  }),
  columnHelper.accessor('bots', {
    header: () => '# of Bots',
    cell: (info) => <BotsCell bots={info.getValue() as string} />,
    footer: (info) => info.column.id
  }),
  columnHelper.accessor('events', {
    header: '# of Events',
    cell: (info) => <DS.Text variant="Body Small/Regular">{info.getValue()}</DS.Text>,
    footer: (info) => info.column.id
  })
]

function toLocalDateInputValue(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getYesterday(): string {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return toLocalDateInputValue(d)
}

function getToday(): string {
  return toLocalDateInputValue(new Date())
}

export const GamesTable = observer(() => {
  const store = useStore()
  const [dataSource, setDataSource] = useState<DataSource>('convex')
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10
  })
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null])
  const [isLoading, setIsLoading] = useState(false)
  const [startDate, setStartDate] = useState(getYesterday)
  const [endDate, setEndDate] = useState(getToday)

  const currentCursor = cursorStack[cursorStack.length - 1]
  const convexCurrentPage = cursorStack.length

  // Reset pagination when switching data sources or filters
  const handleDataSourceChange = (source: DataSource) => {
    setDataSource(source)
    setPagination({ pageIndex: 0, pageSize: 10 })
    setCursorStack([null])
  }

  const handleDateChange = (setter: (v: string) => void) => (value: string) => {
    setter(value)
    setPagination({ pageIndex: 0, pageSize: 10 })
    setCursorStack([null])
  }

  // --- Legacy (GraphQL) data ---
  const legacyPagination = store.dashboard.activeGamesDetails?.pagination || {
    currentPage: 1,
    totalPages: 1
  }

  const legacyData: GameModelType[] = store.dashboard.activeGamesDetails?.games
    ? getSnapshot(store.dashboard.activeGamesDetails?.games || []) || []
    : []

  const fetchGamesPage = useCallback(
    async (page?: number, pageSize?: number) => {
      if (!(page && pageSize)) return
      setIsLoading(true)
      await store.dashboard.getAllGamesDetails({ page, pageSize })
      setIsLoading(false)
    },
    [store.dashboard]
  )

  useEffect(() => {
    if (dataSource === 'legacy') {
      fetchGamesPage(pagination.pageIndex + 1, pagination.pageSize)
    }
  }, [pagination, dataSource])

  // --- Convex data ---
  const startTimestamp = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : undefined
  const endTimestamp = endDate ? new Date(endDate).setHours(23, 59, 59, 999) : undefined

  const convexResult = useQuery(
    api.games.queries.getAllGamesDetails,
    dataSource === 'convex'
      ? {
          paginationOpts: { cursor: currentCursor, numItems: pagination.pageSize },
          startDate: startTimestamp,
          endDate: endTimestamp
        }
      : 'skip'
  )

  const convexData: GameModelType[] = useMemo(() => {
    if (!convexResult?.games) return []
    return convexResult.games.map((game) => ({
      __typename: 'DashboardGame' as const,
      gameId: game.gameId,
      createdAt: typeof game.createdAt === 'number' ? new Date(game.createdAt).toISOString() : game.createdAt,
      gameType: game.gameType,
      hash: game.hash,
      finishedAt: game.finishedAt,
      creator: { __typename: 'Creator' as const, ...game.creator },
      players: game.players.map((p) => ({ __typename: 'PlayerInfo' as const, ...p })),
      bots: game.bots,
      events: game.events
    }))
  }, [convexResult])

  const convexIsDone = convexResult?.isDone ?? true
  const convexContinueCursor = convexResult?.continueCursor ?? null

  // --- Resolved values based on data source ---
  const tableData = dataSource === 'legacy' ? legacyData : convexData
  const currentPage = dataSource === 'legacy' ? legacyPagination.currentPage : convexCurrentPage
  const totalPages = dataSource === 'legacy' ? legacyPagination.totalPages : undefined
  const isTableLoading = dataSource === 'legacy' ? isLoading : dataSource === 'convex' && !convexResult

  const canGoPrev = dataSource === 'legacy' ? pagination.pageIndex > 0 : cursorStack.length > 1
  const canGoNext = dataSource === 'legacy'
    ? pagination.pageIndex < ((legacyPagination.totalPages ?? 1) - 1)
    : !convexIsDone && !!convexContinueCursor

  const handlePrevPage = () => {
    if (dataSource === 'legacy') {
      setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex - 1 }))
    } else {
      setCursorStack((prev) => prev.slice(0, -1))
    }
  }

  const handleNextPage = () => {
    if (dataSource === 'legacy') {
      setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }))
    } else if (convexContinueCursor && !convexIsDone) {
      setCursorStack((prev) => [...prev, convexContinueCursor])
    }
  }

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel()
  })

  return (
    <View style={styles.container}>
      <View style={styles.headerRow}>
        <DS.Text variant="Heading 3/Medium">Games Details</DS.Text>
        <View style={styles.toggleContainer}>
          <Pressable
            style={[styles.toggleButton, styles.toggleButtonLeft, dataSource === 'convex' && styles.toggleButtonActive]}
            onPress={() => handleDataSourceChange('convex')}
          >
            <Text style={[styles.toggleText, dataSource === 'convex' && styles.toggleTextActive]}>Convex</Text>
          </Pressable>
          <Pressable
            style={[
              styles.toggleButton,
              styles.toggleButtonRight,
              dataSource === 'legacy' && styles.toggleButtonActive
            ]}
            onPress={() => handleDataSourceChange('legacy')}
          >
            <Text style={[styles.toggleText, dataSource === 'legacy' && styles.toggleTextActive]}>Legacy</Text>
          </Pressable>
        </View>
      </View>
      {dataSource === 'convex' && (
        <View style={styles.dateFilterRow}>
          <View style={styles.dateFilterGroup}>
            <Text style={styles.dateFilterLabel}>From</Text>
            <input
              type="date"
              value={startDate}
              onChange={(e) => handleDateChange(setStartDate)(e.target.value)}
              style={dateInputStyle}
            />
          </View>
          <View style={styles.dateFilterGroup}>
            <Text style={styles.dateFilterLabel}>To</Text>
            <input
              type="date"
              value={endDate}
              onChange={(e) => handleDateChange(setEndDate)(e.target.value)}
              style={dateInputStyle}
            />
          </View>
        </View>
      )}
      <View style={styles.tableContainer}>
        <View style={styles.tableHeader}>
          {table.getHeaderGroups().map((headerGroup) => (
            <View style={styles.tableRow} key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <View style={styles.tableHeaderCell} key={header.id}>
                  {header.isPlaceholder ? null : (
                    <Text style={styles.headerText}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Text>
                  )}
                </View>
              ))}
            </View>
          ))}
        </View>
        <View style={styles.tableBody}>
          {isTableLoading ? (
            <View style={styles.loadingRow}>
              <DS.Text variant="Body Small/Regular">Loading...</DS.Text>
            </View>
          ) : tableData.length === 0 ? (
            <View style={styles.loadingRow}>
              <DS.Text variant="Body Small/Regular">No results found</DS.Text>
            </View>
          ) : (
            table.getRowModel().rows.map((row) => (
              <View style={styles.tableRow} key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <View style={styles.tableCell} key={cell.id}>
                    <Text style={styles.cellText}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Text>
                  </View>
                ))}
              </View>
            ))
          )}
        </View>
      </View>
      <View style={styles.paginationContainer}>
        <View>
          <DS.Text variant="Body Small/Regular">
            {`Page ${currentPage}${totalPages ? ` of ${totalPages}` : ''}`}
          </DS.Text>
        </View>
        <View style={styles.paginationButtonsContainer}>
          <DS.Button
            variant="Medium/Secondary"
            type="On Dark"
            disabled={isTableLoading || !canGoPrev}
            onPress={handlePrevPage}
          >
            Previous
          </DS.Button>
          <DS.Button
            variant="Medium/Secondary"
            type="On Dark"
            disabled={isTableLoading || !canGoNext}
            onPress={handleNextPage}
          >
            Next Page
          </DS.Button>
        </View>
      </View>
    </View>
  )
})

const dateInputStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 8,
  border: `1px solid ${DS.palette['Brand/Light Grey']}`,
  fontSize: 13,
  fontFamily: 'inherit'
}

const styles = StyleSheet.create({
  container: {
    padding: 24,
    marginBottom: 32,
    borderRadius: 12,
    borderColor: DS.palette['Brand/Light Grey'],
    borderWidth: 1,
    backgroundColor: DS.palette['Global/White']
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  toggleContainer: {
    flexDirection: 'row',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    overflow: 'hidden'
  },
  toggleButton: {
    paddingVertical: 6,
    paddingHorizontal: 16
  },
  toggleButtonLeft: {
    borderTopLeftRadius: 20,
    borderBottomLeftRadius: 20
  },
  toggleButtonRight: {
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20
  },
  toggleButtonActive: {
    backgroundColor: DS.palette['Global/Black']
  },
  toggleText: {
    fontSize: 13,
    fontWeight: '500',
    color: DS.palette['Brand/Dark Grey']
  },
  toggleTextActive: {
    color: DS.palette['Global/White']
  },
  loadingRow: {
    padding: 24,
    alignItems: 'center'
  },
  tableContainer: {
    width: '100%',
    borderRadius: 12,
    overflow: 'hidden',
    marginTop: 16,
    marginBottom: 16,
    borderColor: DS.palette['Brand/Light Grey'],
    borderWidth: 1
  },
  tableHeader: {
    backgroundColor: '#eeedea'
  },
  tableBody: {
    backgroundColor: 'white'
  },
  tableRow: {
    flexDirection: 'row'
  },
  tableHeaderCell: {
    flex: 1,
    borderBottomWidth: 2,
    borderBottomColor: '#dee2e6',
    padding: 16
  },
  headerText: {
    fontWeight: '600',
    color: DS.palette['Brand/Dark Grey']
  },
  tableCell: {
    flex: 1,
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef'
  },
  cellText: {
    color: '#212529'
  },

  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  buttonText: {
    color: 'white',
    fontWeight: '500'
  },
  paginationButtonsContainer: {
    flexDirection: 'row',
    gap: 8
  },
  player: {
    gap: 4,
    borderColor: '#c3efff',
    borderWidth: 1,
    borderRadius: 99,
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: '#effaff'
  },
  playersContainer: {
    gap: 4,
    flexDirection: 'row',
    flexWrap: 'wrap'
  },
  dateFilterRow: {
    flexDirection: 'row',
    gap: 16,
    marginTop: 12,
    alignItems: 'center'
  },
  dateFilterGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8
  },
  dateFilterLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: DS.palette['Brand/Dark Grey']
  },
  botsContainer: {
    backgroundColor: '#ffe2da',
    color: DS.palette['Admin/Icon'],
    padding: 8,
    borderRadius: 99
  }
})
